import React, { useState, useEffect, useMemo } from 'react';
import RTDropdownButton from './RTDropdownButton';
import RTSpinner from './RTSpinner';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { UI_CONFIG } from '../../config/appConfig';
import { buildExportFileName } from '../../utils/dateHelper';
import './Table.css';

const DEFAULT_PAGE_SIZE = UI_CONFIG.itemsPerPage || 10;
const DEFAULT_PAGE_SIZES = [10, 20, 50, 100];

const getColumnStyle = (col) => ({
  ...(col.width ? { width: col.width, minWidth: col.width, maxWidth: col.width } : {}),
  ...(col.align ? { textAlign: col.align } : {}),
});

/** Returns array where positive values are rowSpan, -1 means skip cell (merged above). */
const computeRowSpans = (rows, groupKey) => {
  const spans = rows.map(() => 0);
  let i = 0;
  while (i < rows.length) {
    let j = i + 1;
    while (j < rows.length && rows[j][groupKey] === rows[i][groupKey]) {
      j++;
    }
    spans[i] = j - i;
    for (let k = i + 1; k < j; k++) {
      spans[k] = -1;
    }
    i = j;
  }
  return spans;
};

const Table = ({
  columns = [],
  data = [],
  title = '',
  showSearch = true,
  exportable = true,
  showPagination = true,
  noDataText = "No data found.",
  onRowClick,
  pageSize: initialPageSize, // New prop for initial page size
  showTotalCount = false, // New prop to show total count
  totalCountText = 'Total Row count is', // New prop for custom count text
  exportFileName = '', // New prop for export file naming
  rowGroupConfig = null, // Optional: { groups: [{ key: 'room_id', columns: ['room_name'] }] }
  disableSort = false,
  loading = false,
  loadingMessage = 'Loading...',
}) => {
  // Validate and sanitize the initial page size
  const validatedPageSize = (initialPageSize && Number.isInteger(initialPageSize) && initialPageSize > 0) 
    ? initialPageSize 
    : DEFAULT_PAGE_SIZE;
    
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(validatedPageSize);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');

  // Reset page when data changes
  useEffect(() => {
    setPage(1);
  }, [data]);

  // Update the useEffect for pageSize changes
  useEffect(() => {
    if (initialPageSize && Number.isInteger(initialPageSize) && initialPageSize > 0 && initialPageSize !== pageSize) {
      setPageSize(initialPageSize);
      setPage(1); // Reset to first page when page size changes
    }
  }, [initialPageSize]);

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!search) return data;
    return data.filter(row =>
      columns.some(col => {
        const cellValue = col.searchValue
          ? col.searchValue(row)
          : col.exportValue
            ? col.exportValue(row)
            : row[col.accessor];
        return cellValue?.toString().toLowerCase().includes(search.toLowerCase());
      })
    );
  }, [data, search, columns]);

  // Sort data
  const sortedData = useMemo(() => {
    if (disableSort || !sortBy) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortBy, sortOrder, disableSort]);

  // Paginate data
  const totalRows = sortedData.length;
  const totalPages = Math.ceil(totalRows / pageSize);
  const paginatedData = sortedData.slice((page - 1) * pageSize, page * pageSize);

  const pageRowSpanMaps = useMemo(() => {
    if (!rowGroupConfig?.groups?.length) return null;

    const maps = {};
    rowGroupConfig.groups.forEach(({ key, columns }) => {
      const spans = computeRowSpans(paginatedData, key);
      maps[key] = spans;
      columns.forEach(colAccessor => {
        maps[colAccessor] = spans;
      });
    });
    return maps;
  }, [paginatedData, rowGroupConfig]);

  const handleSort = (accessor) => {
    if (disableSort) return;
    if (sortBy === accessor) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(accessor);
      setSortOrder('asc');
    }
  };

  const getSortIndicator = (accessor) => {
    if (sortBy !== accessor) return ' ↕';
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  // Also update handlePageSizeChange to validate
  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    if (newSize > 0) {
      setPageSize(newSize);
      setPage(1);
    }
  };

  const getExportBaseName = () => {
    if (exportFileName) return exportFileName;
    if (typeof title === 'string' && title.trim()) return title;
    return 'table';
  };

  const handleExport = () => {
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '';
      
      let str = value.toString();
      
      // Remove or replace problematic characters that can corrupt CSV
      str = str
        .replace(/[#"',\n\r\t]/g, ' ')  // Replace #, quotes, commas, newlines, tabs with space
        .replace(/\s+/g, ' ')          // Replace multiple spaces with single space
        .trim();                       // Remove leading/trailing spaces
      
      return str;
    };
    
    // Filter out Action columns and columns with disableExport flag
    const exportableColumns = columns.filter(col => {
      if (col.disableExport) return false;
      const header = col.Header?.toLowerCase() || '';
      return !header.includes('action') && col.accessor; // Exclude Action columns and columns without accessor
    });
    
    // Note: CSV format doesn't support formatting (bold, colors, etc.)
    // For formatted output with styled headers, use PDF export instead
    const csvRows = [
      exportableColumns.map(col => escapeCSV(col.Header)).join(','),
      ...filteredData.map(row =>
        exportableColumns.map(col => {
          // Use exportValue function if provided, otherwise use raw accessor value
          const value = col.exportValue ? col.exportValue(row) : row[col.accessor];
          return escapeCSV(value);
        }).join(',')
      ),
    ];
    const csvContent = 'data:text/csv;charset=utf-8,' + csvRows.join('\n');
    const link = document.createElement('a');
    link.href = encodeURI(csvContent);
    link.download = buildExportFileName(getExportBaseName(), 'csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPDF = () => {
    // Filter out Action columns and columns with disableExport flag
    const exportableColumns = columns.filter(col => {
      if (col.disableExport) return false;
      const header = col.Header?.toLowerCase() || '';
      return !header.includes('action') && col.accessor; // Exclude Action columns and columns without accessor
    });
    
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(getExportBaseName(), 14, 16);
    
    doc.autoTable({
      head: [exportableColumns.map(col => col.Header)],
      body: filteredData.map(row => 
        exportableColumns.map(col => {
          // Use exportValue function if provided, otherwise use raw accessor value
          const value = col.exportValue ? col.exportValue(row) : row[col.accessor];
          return value ?? '';
        })
      ),
      startY: 25,
      styles: { 
        fontSize: 10,
        cellPadding: 3
      },
      headStyles: { 
        fillColor: [21, 60, 127], // Dark blue background
        textColor: [255, 255, 255], // White text
        fontStyle: 'bold',
        fontSize: 11,
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 9
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245] // Light gray for alternate rows
      }
    });
    doc.save(buildExportFileName(getExportBaseName(), 'pdf'));
  };

  return (
    <div className="rt-table-container">
      <div className="rt-table-header">
        <div className="rt-table-header-left">
          <h2 className="rt-table-title">{title}</h2>
          {showTotalCount && filteredData.length > 0 && (
            <div className="rt-table-count">
              {totalCountText} {filteredData.length}
            </div>
          )}
        </div>
        <div className="rt-table-actions">
          {showSearch && (
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="rt-table-search"
            />
          )}
          {exportable && paginatedData.length > 0 && (
            <RTDropdownButton
              label="Export"
              options={[
                { label: 'Export as CSV', onClick: handleExport },
                { label: 'Export as PDF', onClick: handleExportPDF }
              ]}
            />
          )}
        </div>
      </div>
      {loading ? (
        <div className="rt-table-loading">
          <RTSpinner fullScreen={false} />
          <p>{loadingMessage}</p>
        </div>
      ) : (
      <div className="rt-table-wrapper">
        <table className="rt-table">
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={col.accessor}
                  className="rt-table-th"
                  onClick={col.Header && typeof col.Header === 'function' ? undefined : () => handleSort(col.accessor)}
                  style={{
                    ...(disableSort || (col.Header && typeof col.Header === 'function') ? { cursor: 'default' } : { cursor: 'pointer' }),
                    ...getColumnStyle(col),
                  }}
                >
                  {typeof col.Header === 'function' 
                    ? col.Header({ value: null, data: paginatedData })
                    : col.Header
                  }
                  {col.Header && typeof col.Header !== 'function' && !disableSort && (
                    <span className="rt-table-sort">{getSortIndicator(col.accessor)}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="rt-table-empty" style={{ textAlign: 'center' }}>
                  {search 
                    ? `No rows found for the search criteria "${search}".`
                    : noDataText
                  }
                </td>
              </tr>
            ) : (
              paginatedData.map((row, idx) => (
                <tr
                  key={row.allocation_id || row.id || idx}
                  className={idx % 2 === 0 ? 'rt-table-row-even' : 'rt-table-row-odd'}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={onRowClick ? { cursor: 'pointer' } : undefined}
                >
                  {columns.map(col => {
                    const spanInfo = pageRowSpanMaps?.[col.accessor];
                    if (spanInfo && spanInfo[idx] === -1) {
                      return null;
                    }

                    return (
                    <td 
                      key={col.accessor} 
                      className={`rt-table-td${spanInfo && spanInfo[idx] > 1 ? ' rt-table-td-grouped' : ''}`}
                      rowSpan={spanInfo && spanInfo[idx] > 1 ? spanInfo[idx] : undefined}
                      style={getColumnStyle(col)}
                    >
                      {col.Cell
                        ? col.Cell({ value: row[col.accessor], row })
                        : row[col.accessor] ?? ''}
                    </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}
      {!loading && showPagination && totalRows > 0 && (
        <div className="rt-table-pagination">
          {totalRows > DEFAULT_PAGE_SIZE && (
          <div>
            Rows per page:&nbsp;
            <select value={pageSize} onChange={handlePageSizeChange}>
              {[
                ...(initialPageSize && !DEFAULT_PAGE_SIZES.includes(initialPageSize) ? [initialPageSize] : []),
                ...DEFAULT_PAGE_SIZES,
                totalRows
              ]
                .filter((v, i, arr) => arr.indexOf(v) === i && v <= totalRows && v > 0)
                .sort((a, b) => a - b)
                .map(opt => (
                  <option key={opt} value={opt}>
                    {opt === totalRows ? `All (${totalRows})` : opt}
                  </option>
                ))
              }
            </select>
          </div>
          )}
          {totalRows > pageSize && (
          <div>
            <a
              href="#prev"
              className={`rt-table-page-link${page === 1 ? ' disabled' : ''}`}
              onClick={e => {
                e.preventDefault();
                if (page > 1) setPage(page - 1);
              }}
            >
              Prev
            </a>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p =>
                p === 1 ||
                p === totalPages ||
                (p >= page - 2 && p <= page + 2)
              )
              .reduce((acc, p, i, arr) => {
                if (i > 0 && p - arr[i - 1] > 1) acc.push('ellipsis');
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === 'ellipsis' ? (
                  <span key={`ellipsis-${idx}`} style={{ margin: '0 4px' }}>…</span>
                ) : (
                  <a
                    key={p}
                    href={`#page-${p}`}
                    className={`rt-table-page-link${page === p ? ' active' : ''}`}
                    style={{
                      fontWeight: page === p ? 700 : 400,
                      textDecoration: page === p ? 'underline' : 'none',
                      color: page === p ? '#153C7F' : undefined,
                      margin: '0 4px'
                    }}
                    onClick={e => {
                      e.preventDefault();
                      setPage(p);
                    }}
                  >
                    {p}
                  </a>
                )
              )
            }
            <a
              href="#next"
              className={`rt-table-page-link${page === totalPages ? ' disabled' : ''}`}
              onClick={e => {
                e.preventDefault();
                if (page < totalPages) setPage(page + 1);
              }}
            >
              Next
            </a>
          </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Table;